import React, { useEffect, useRef, useState } from 'react';
import uparrow from '../../assets/images/up.png';
import downarrow from '../../assets/images/down.png';
import { useDispatch, useSelector } from 'react-redux';
import { authActions } from '../../_store';

const ScrollContainer = ({ children, className, isSendECard, ...props }) => {
  const ECardloadMoreCount = useSelector((state) => state.auth?.ECardloadMoreCount);
  const contentRef = useRef(null);
  const scrollTrackRef = useRef(null);
  const scrollThumbRef = useRef(null);

  const [thumbHeight, setThumbHeight] = useState(20);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [scrollStartPosition, setScrollStartPosition] = useState(null);
  const [initialScrollTop, setInitialScrollTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dispatch = useDispatch();

  const checkOverflow = () => {
    if (contentRef.current) {
      const { scrollHeight, clientHeight } = contentRef.current;
      setIsOverflowing(scrollHeight > clientHeight);
    }
  };

  const handleResize = (ref, trackSize) => {
    if (ref) {
      const { clientHeight, scrollHeight } = ref;
      setThumbHeight(Math.max((clientHeight / scrollHeight) * trackSize, 20));
    }
  };

  const handleScrollButton = (direction) => {
    if (contentRef.current) {
      const scrollAmount = direction === 'down' ? 200 : -200;
      contentRef.current.scrollBy({ top: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleTrackClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const trackCurrent = scrollTrackRef.current;
    const contentCurrent = contentRef.current;
    if (trackCurrent && contentCurrent) {
      const { clientY } = e;
      const rect = trackCurrent.getBoundingClientRect();
      const trackTop = rect.top;
      const thumbOffset = -(thumbHeight / 2);
      const clickRatio = (clientY - trackTop + thumbOffset) / trackCurrent.clientHeight;
      const scrollAmount = Math.floor(clickRatio * contentCurrent.scrollHeight);
      contentCurrent.scrollTo({ top: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleThumbPosition = () => {
    if (contentRef.current && scrollTrackRef.current && scrollThumbRef.current) {
      const newTop = (contentRef.current.scrollTop / contentRef.current.scrollHeight) * scrollTrackRef.current.clientHeight;
      const topPosition = Math.min(newTop, scrollTrackRef.current.clientHeight - thumbHeight);
      scrollThumbRef.current.style.top = `${topPosition}px`;
    }
  };

  const handleThumbMousedown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setScrollStartPosition(e.clientY);
    setInitialScrollTop(contentRef.current.scrollTop);
    setIsDragging(true);
  };

  const handleThumbMousemove = (e) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      const deltaY = (e.clientY - scrollStartPosition) * (contentRef.current.offsetHeight / thumbHeight);
      const newScrollTop = Math.min(initialScrollTop + deltaY, contentRef.current.scrollHeight - contentRef.current.offsetHeight);
      contentRef.current.scrollTop = newScrollTop;
    }
  };

  const handleThumbMouseup = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, clientHeight, scrollHeight } = contentRef.current;
      if (scrollTop + clientHeight >= scrollHeight) {
        if (isSendECard) {
          const UpdateECardloadMoreCount = ECardloadMoreCount + 1;
          dispatch(authActions.updateScroll(UpdateECardloadMoreCount));
        }
      }
    }
  };

  useEffect(() => {
    const trackSize = scrollTrackRef.current?.clientHeight || 0;

    const resizeObserver = new ResizeObserver(() => {
      handleResize(contentRef.current, trackSize);
      checkOverflow();
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
      contentRef.current.addEventListener('scroll', handleThumbPosition);
      contentRef.current.addEventListener('scroll', handleScroll); // Add scroll listener
    }

    window.addEventListener('resize', checkOverflow);
    document.addEventListener('mousemove', handleThumbMousemove);
    document.addEventListener('mouseup', handleThumbMouseup);
    document.addEventListener('mouseleave', handleThumbMouseup);

    checkOverflow(); // Initial check for overflow

    return () => {
      if (contentRef.current) {
        resizeObserver.unobserve(contentRef.current);
        contentRef.current.removeEventListener('scroll', handleThumbPosition);
        contentRef.current.removeEventListener('scroll', handleScroll); // Remove scroll listener
      }

      window.removeEventListener('resize', checkOverflow);
      document.removeEventListener('mousemove', handleThumbMousemove);
      document.removeEventListener('mouseup', handleThumbMouseup);
      document.removeEventListener('mouseleave', handleThumbMouseup);
    };
  }, [handleThumbMousemove, handleThumbMouseup, isDragging]);

  return (
    <div className={isOverflowing ? `custom-scrollbars__container ${className}` : ":"}>
      <div className="custom-scrollbars__content" ref={contentRef} {...props}>
        {children}
      </div>
      {isOverflowing && (
        <div className="custom-scrollbars__scrollbar">
          <button className="scroll-btn" onClick={() => handleScrollButton('up')}>
            <span><img src={uparrow} alt="up" /></span>
          </button>
          <div className="custom-scrollbars__track-and-thumb">
            <div
              className="custom-scrollbars__track"
              ref={scrollTrackRef}
              onClick={handleTrackClick}
              style={{ cursor: isDragging ? 'grabbing' : 'default' }}
            ></div>
            <div
              className="custom-scrollbars__thumb"
              ref={scrollThumbRef}
              onMouseDown={handleThumbMousedown}
              style={{
                height: `${thumbHeight}px`,
                cursor: isDragging ? 'grabbing' : 'grab',
              }}
            ></div>
          </div>
          <button className="scroll-btn" onClick={() => handleScrollButton('down')}>
            <span><img src={downarrow} alt="down" /></span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ScrollContainer;
